import tinycolor from 'tinycolor2';
import { Theme } from '../classes/theme';

export const darkenColor =
  (property: string, amount: number = 1) =>
  (theme: Theme) =>
    tinycolor(theme.get(property) as string)
      .darken(amount)
      .toHex8String();

export const lightenColor =
  (property: string, amount: number = 10) =>
  (theme: Theme) =>
    tinycolor(theme.get(property) as string)
      .lighten(amount)
      .toHex8String();

export const offsetColor = (property: string, amount: number) => {
  return (theme: Theme) =>
    tinycolor(theme.get(property) as string)
      [theme.get('contrast-direction') as 'lighten' | 'darken'](amount)
      .toHex8String();
};

export const colorOpacity = (reference: ReturnType<typeof ref>, opacity: number = 0.9) => {
  return (theme: Theme) => tinycolor(reference(theme)).setAlpha(opacity).toHex8String();
};

export const ref = (property: string) => (theme: Theme) => theme.get(property) as string;

export const base = Theme.create('base')
  .property('color-scheme', 'light')
  .property('contrast-direction', 'darken')

  /**
   * Base
   */
  .property('primary.color', '#0aadff')
  .property('success.color', '#4ade80')
  .property('warning.color', '#efc005')
  .property('danger.color', '#f87171')
  .property('success.background.color', '#f1fdf4')
  .property('warning.background.color', '#fefce7')
  .property('danger.background.color', '#fdf2f2')
  .property('background.color', '#fff')

  .property('page.radius', '0.5rem')
  .property('outer.radius', '0.5rem')
  .property('inner.radius', '0.5rem')

  /**
   * Scrollbar
   */
  .property('scrollbar.width', '4px')
  .property('scrollbar.height', '16px')
  .property('scrollbar.track.color', offsetColor('background.color', 4))
  .property('scrollbar.thumb.color', offsetColor('background.color', 18))
  .property('scrollbar.thumb.border.radius', '2px')

  /**
   * Text
   */
  .property('text.color', '#2c2d2d')
  .property('text.muted.color', lightenColor('text.color', 50))
  .property('text.font.family', 'Source Sans Pro')
  .property('text.font.weight', '200;300;400;600;700')
  .property('heading.font.family', 'Poppins')
  .property('heading.font.weight', '700')

  /**
   * Image
   */
  .property('image.background.color', offsetColor('card.background.color', 5))

  /**
   * Skeleton
   */
  .property('skeleton.color', offsetColor('card.background.color', 10))

  /**
   * Links
   */
  .property('link.text.color', ref('primary.color'))
  .property('link.hover.text.color', offsetColor('link.text.color', 10))

  /**
   * Header
   */
  .property('header.text.color', ref('text.color'))
  .property('header.background.color', ref('background.color'))
  .property('header.border.color', offsetColor('background.color', 10))
  .property('header.border.width', '1px')

  /**
   * Menu
   */
  .property('menu.text.color', offsetColor('text.color', -20))
  .property('menu.hover.text.color', ref('text.color'))
  .property('menu.background.color', ref('background.color'))
  .property('menu.border.color', offsetColor('background.color', 10))
  .property('menu.border.width', '1px')
  .property('menu.active.text.color', ref('primary.color'))
  .property('menu.hover.background.color', colorOpacity(offsetColor('menu.background.color', 5)))

  /**
   * Navbar
   */
  .property('navbar.text.color', ref('text.color'))
  .property('navbar.background.color', colorOpacity(lightenColor('background.color', 5)))
  .property('navbar.border.color', offsetColor('background.color', 10))
  .property('navbar.border.width', '1px')
  .property('navbar.active.text.color', ref('primary.color'))
  .property(
    'navbar.hover.background.color',
    colorOpacity(offsetColor('navbar.background.color', 5)),
  )

  /**
   * Page
   */
  .property('page.background.color', ref('background.color'))

  /**
   * Card
   */
  .property('card.text.color', ref('text.color'))
  .property('card.background.color', offsetColor('background.color', 3))
  .property('card.hover.background.color', offsetColor('card.background.color', 3))
  .property('card.border.color', offsetColor('card.background.color', 5))
  .property('card.border.width', '0px')
  .property('card.radius', ref('outer.radius'))
  .property('card.inner.radius', ref('inner.radius'))

  /**
   * Pod
   */
  .property('pod.background.color', ref('background.color'))
  .property('pod.border.width', '1px')

  /**
   * Forms
   */
  .property('input.text.color', ref('text.color'))
  .property('input.background.color', 'transparent')
  .property('input.border.color', offsetColor('background.color', 20))
  .property('input.border.width', '1px')
  .property('input.focus.border.color', ref('primary.color'))
  .property('input.focus.border.width', '2px')
  .property('input.radius', ref('inner.radius'))
  .property('input.hover.background.color', offsetColor('card.background.color', 3))
  .property('input.active.background.color', ref('card.background.color'))

  /**
   * Select Options
   */
  .property('select.option.text.color', ref('text.color'))
  .property('select.option.background.color', ref('card.background.color'))
  .property('select.option.checked.background.color', ref('primary.color'))
  .property('select.option.checked.text.color', '#fff')

  /**
   * Rich text
   */
  .property('editor.placeholder.color', colorOpacity(ref('text.color'), 0.5))

  /**
   * Code blocks
   */
  .property('code.background.color', '#282c34')
  .property('code.text.color', '#ccd5e4')
  .property('code.mono.1', '#abb2bf')
  .property('code.mono.2', '#818896')
  .property('code.mono.3', '#5c6370')
  .property('code.hue.1', '#56b6c2')
  .property('code.hue.2', '#61aeee')
  .property('code.hue.3', '#c678dd')
  .property('code.hue.4', '#98c379')
  .property('code.hue.5', '#e06c75')
  .property('code.hue.5.2', '#be5046')
  .property('code.hue.6', '#d19a66')
  .property('code.hue.6.2', '#e6c07b')

  .property('code.inline.background.color', offsetColor('card.background.color', 11))
  .property('code.inline.text.color', ref('text.color'))
  .property('code.inline.border.radius', '0.5rem')
  .property('code.inline.padding.x', '0.5rem')
  .property('code.inline.padding.y', '0.125rem')

  .property('code.block.background.color', ref('code.background.color'))
  .property('code.block.text.color', ref('code.text.color'))
  .property('code.block.border.radius', '0.5rem')
  .property('code.block.padding.x', '1rem')
  .property('code.block.padding.y', '1rem')

  .property('code.block.copy.text.color', ref('code.text.color'))
  .property('code.block.copy.background.color', darkenColor('code.background.color', 5))
  .property('code.block.copy.border.radius', ref('code.block.border.radius'))
  .property('code.block.copy.padding.x', '0.5rem')
  .property('code.block.copy.padding.y', '0.25rem')
  .property('code.block.copy.shadow', '0 0 0.5rem rgba(0, 0, 0, 0.1)')

  /**
   * Button
   */
  .property('button.primary.text.color', '#fff')
  .property('button.primary.background.color', ref('primary.color'))
  .property(
    'button.primary.hover.background.color',
    lightenColor('button.primary.background.color'),
  )
  .property('button.primary.border.color', ref('button.primary.background.color'))
  .property('button.primary.border.width', '1px')
  .property('button.primary.active.border.color', offsetColor('button.primary.border.color', 20))

  .property('button.secondary.text.color', ref('text.color'))
  .property('button.secondary.background.color', 'transparent')
  .property('button.secondary.hover.background.color', '#0001')
  .property('button.secondary.border.color', offsetColor('text.color', -30))
  .property('button.secondary.border.width', '1px')
  .property(
    'button.secondary.active.border.color',
    offsetColor('button.secondary.border.color', 10),
  )

  .property('button.success.text.color', darkenColor('success.color', 40))
  .property('button.success.background.color', ref('success.color'))
  .property(
    'button.success.hover.background.color',
    lightenColor('button.success.background.color'),
  )
  .property('button.success.border.color', ref('button.success.background.color'))
  .property('button.success.border.width', '1px')
  .property('button.success.active.border.color', offsetColor('button.success.border.color', 20))

  .property('button.warning.text.color', darkenColor('warning.color', 40))
  .property('button.warning.background.color', ref('warning.color'))
  .property(
    'button.warning.hover.background.color',
    lightenColor('button.warning.background.color'),
  )
  .property('button.warning.border.color', ref('button.warning.background.color'))
  .property('button.warning.border.width', '1px')
  .property('button.warning.active.border.color', offsetColor('button.warning.border.color', 20))

  .property('button.danger.text.color', darkenColor('danger.color', 60))
  .property('button.danger.background.color', ref('danger.color'))
  .property('button.danger.hover.background.color', lightenColor('button.danger.background.color'))
  .property('button.danger.border.color', ref('button.danger.background.color'))
  .property('button.danger.border.width', '1px')
  .property('button.danger.active.border.color', offsetColor('button.danger.border.color', 20))

  .property('button.radius', ref('inner.radius'))

  /**
   * Tags
   */
  .property('tag.text.color', ref('text.color'))
  .property('tag.border.width', '1px')
  .property('tag.radius', '999px')

  .property('tag.default.background.color', offsetColor('card.background.color', 3))
  .property('tag.default.border.color', offsetColor('tag.default.background.color', 1))

  .property('tag.success.text.color', offsetColor('success.color', 20))
  .property('tag.success.background.color', ref('success.background.color'))
  .property('tag.success.border.color', offsetColor('success.color', 1))

  .property('tag.warning.text.color', offsetColor('warning.color', 30))
  .property('tag.warning.background.color', ref('warning.background.color'))
  .property('tag.warning.border.color', offsetColor('warning.color', 1))

  .property('tag.danger.text.color', offsetColor('danger.color', 10))
  .property('tag.danger.background.color', ref('danger.background.color'))
  .property('tag.danger.border.color', offsetColor('danger.color', 1))

  /**
   * Status
   */
  .property('status.success.background.color', 'rgb(220, 252, 231)')
  .property('status.warning.background.color', 'rgb(254, 249, 195)')
  .property('status.danger.background.color', 'rgb(254, 226, 226)')
  .property('status.default.background.color', 'rgb(243, 244, 246)')

  .property('status.success.text.color', 'rgb(22, 101, 52)')
  .property('status.warning.text.color', 'rgb(113, 63, 18)')
  .property('status.danger.text.color', 'rgb(127, 29, 29)')
  .property('status.default.text.color', 'rgb(17, 24, 39)')

  .property('status.success.border.color', darkenColor('status.success.background.color', 5))
  .property('status.warning.border.color', darkenColor('status.warning.background.color', 5))
  .property('status.danger.border.color', darkenColor('status.danger.background.color', 5))
  .property('status.default.border.color', darkenColor('status.default.background.color', 5))

  .property('status.success.icon.color', 'rgb(34, 197, 94)')
  .property('status.warning.icon.color', 'rgb(234, 179, 8)')
  .property('status.danger.icon.color', 'rgb(239, 68, 68)')
  .property('status.default.icon.color', 'rgb(107, 114, 128)')

  /**
   * Tabs
   */
  .property('tabs.border.color', offsetColor('card.background.color', 10))
  .property('tabs.border.width', '1px')
  .property('tab.text.color', ref('text.color'))
  .property('tab.background.color', 'transparent')
  .property('tab.border.color', offsetColor('card.background.color', 10))
  .property('tab.border.width', '0px')
  .property('tab.hover.background.color', offsetColor('card.background.color', 3))
  .property('tab.active.background.color', ref('card.background.color'))
  .property('tab.active.border.color', ref('primary.color'))
  .property('tab.active.border.width', '3px')
  .property('tab.radius', ref('inner.radius'))

  /**
   * Steps
   */
  .property('step.border.color', offsetColor('card.background.color', 10))
  .property('step.border.width', '3px')
  .property('step.radius', ref('inner.radius'))

  .property('step.title.color', offsetColor('background.color', 30))
  .property('step.title.active.color', ref('text.color'))
  .property('step.title.completed.color', ref('text.color'))

  .property('step.text.color', offsetColor('background.color', 30))
  .property('step.text.active.color', offsetColor('background.color', 30))
  .property('step.text.completed.color', offsetColor('background.color', 30))

  .property('step.bubble.background.color', ref('background.color'))

  .property('step.bubble.active.border.color', ref('primary.color'))
  .property('step.bubble.active.text.color', ref('text.color'))

  .property('step.bubble.completed.border.color', ref('primary.color'))
  .property('step.bubble.completed.text.color', ref('background.color'))
  .property('step.bubble.completed.background.color', ref('primary.color'))

  /**
   * Table
   */
  .property('table.header.text.color', ref('text.color'))
  .property('table.header.background.color', 'transparent')
  .property('table.header.border.color', offsetColor('card.background.color', 10))
  .property('table.header.border.width', '1px')
  .property('table.row.text.color', ref('text.color'))
  .property('table.row.radius', ref('inner.radius'))
  .property('table.row.border.width', '0px')
  .property('table.row.border.color', offsetColor('card.background.color', 5))
  .property('table.row.odd.background.color', 'transparent')
  .property(
    'table.row.even.background.color',
    colorOpacity(offsetColor('card.background.color', 10), 0.25),
  )

  /**
   * Alerts
   */
  .property('alert.default.text.color', offsetColor('text.color', -10))
  .property('alert.default.background.color', offsetColor('background.color', 3))
  .property('alert.default.border.color', offsetColor('text.color', -10))

  .property('alert.success.text.color', offsetColor('success.color', 20))
  .property('alert.success.background.color', ref('success.background.color'))
  .property('alert.success.border.color', ref('alert.success.text.color'))

  .property('alert.warning.text.color', offsetColor('warning.color', 30))
  .property('alert.warning.background.color', ref('warning.background.color'))
  .property('alert.warning.border.color', ref('alert.warning.text.color'))

  .property('alert.danger.text.color', offsetColor('danger.color', 10))
  .property('alert.danger.background.color', ref('danger.background.color'))
  .property('alert.danger.border.color', ref('alert.danger.text.color'))

  .property('alert.border.width', '0px')
  .property('alert.radius', ref('inner.radius'))

  /**
   * Bubble
   */
  .property('bubble.text.color', ref('text.color'))
  .property('bubble.background.color', offsetColor('background.color', 3))
  .property('bubble.border.color', offsetColor('background.color', 10))
  .property('bubble.border.width', '1px')
  .property('bubble.radius', '999px')

  /**
   * Nudge
   */
  .property('nudge.background.color', '#2c2d2d')
  .property('nudge.text.color', '#fff')
  .property('nudge.border.radius', '8px')
  .property('nudge.completion.bar.color', ref('primary.color'))
  .property('nudge.padding', '8px 24px')
  .property('nudge.error.background.color', 'rgb(254, 226, 226)')
  .property('nudge.error.text.color', 'rgb(185, 28, 28)')
  .property('nudge.error.completion.bar.color', 'rgb(252, 165, 165)')
  .property('nudge.warning.text.color', offsetColor('warning.color', 30))
  .property('nudge.warning.background.color', ref('warning.background.color'))
  .property('nudge.warning.completion.bar.color', offsetColor('warning.color', -10))
  .property('nudge.success.text.color', offsetColor('success.color', 20))
  .property('nudge.success.background.color', ref('success.background.color'))
  .property('nudge.success.completion.bar.color', offsetColor('success.color', -20))
  /**
   * Description list
   */
  .property('descriptionList.spacing', '0.35rem')
  .property('descriptionList.divide.color', offsetColor('card.background.color', 10))

  /**
   * Dropdown
   */
  .property('dropdown.background.color', ref('page.background.color'))
  .property('dropdown.ring.color', darkenColor('card.background.color', 5))
  .property('dropdown.action.text.color', lightenColor('text.color', 5))
  .property('dropdown.action.hover.text.color', ref('text.color'))
  .property('dropdown.action.background.color', ref('dropdown.background.color'))
  .property('dropdown.action.hover.background.color', darkenColor('dropdown.background.color', 5))

  /**
   * Pip
   */
  .property('pip.text.color', '#fff')
  .property('pip.background.color', ref('primary.color'))

  /**
   * Chat
   */
  .property('chat.background.color', darkenColor('page.background.color'))
  .property('chat.message.text.color', ref('card.text.color'))
  .property('chat.message.background.color', offsetColor('card.background.color', 2))
  .property('chat.message.border.color', darkenColor('chat.message.background.color', 2))
  .property('chat.self.text.color', 'text.color')
  .property('chat.self.background.color', offsetColor('primary.color', -40))
  .property('chat.self.border.color', darkenColor('chat.self.background.color', 5))
  .property('chat.hidden.text.color', '#fdecf3')
  .property('chat.hidden.background.color', '#fdecf3')
  .property('chat.hidden.border.color', darkenColor('chat.hidden.background.color', 2))
  .property('chat.focused.text.color', '#fdecf3')
  .property('chat.focused.background.color', offsetColor('primary.color', -40))
  .property('chat.focused.border.color', colorOpacity(ref('primary.color'), 0.5))
  .property('chat.marker.new.text.color', '#ef5859')
  .property('chat.marker.new.border.color', ref('chat.marker.new.text.color'))
  .property('chat.marker.date.text.color', colorOpacity(ref('text.color'), 0.4))
  .property('chat.marker.date.border.color', colorOpacity(ref('chat.marker.date.text.color'), 0.2))

  .property('chat.icon.border.color', darkenColor('card.background.color', 5))

  .property('chat.message.rich.tag.background.color', ref('background.color'))
  .property('chat.message.rich.tag.border.color', ref('primary.color'))
  .property('chat.message.rich.tag.text.color', ref('primary.color'))
  .property('chat.message.rich.tag.border.radius', '9999px')
  .property('chat.message.rich.tag.padding.x', '0.35rem')

  /**
   * Charts
   */
  .property('chart.data.colors', [
    '#233c4b',
    '#506e00',
    '#fad223',
    '#007da5',
    '#05d7a0',
    '#c34637',
    '#82d2f5',
    '#ff7d2d',
    '#506e00',
    '#37234b',
    '#beaf5f',
    '#ff8c96',
    '#0a509b',
  ])

  /**
   * Metric indicators
   */
  .property('metrics.neutral.color', ref('card.background.color'))
  .property('metrics.neutral.background.color', 'transparent')
  .property('metrics.neutral.text.color', ref('text.color'))
  .property('metrics.neutral.border.color', ref('input.border.color'))
  .property('metrics.passing.color', '#86b302')
  .property('metrics.passing.background.color', darkenColor('metrics.passing.color', 20))
  .property('metrics.passing.text.color', '#fff')
  .property('metrics.passing.border.color', 'transparent')
  .property('metrics.warning.color', '#f89815')
  .property('metrics.warning.background.color', darkenColor('metrics.warning.color', 20))
  .property('metrics.warning.text.color', '#fff')
  .property('metrics.warning.border.color', 'transparent')
  .property('metrics.failing.color', '#dd176d')
  .property('metrics.failing.background.color', darkenColor('metrics.failing.color', 20))
  .property('metrics.failing.text.color', '#fff')
  .property('metrics.failing.border.color', 'transparent')

  /**
   * Workflow
   */
  .property('workflow.inprogress.text.color', ref('warning.color'))
  .property('workflow.finishing.text.color', ref('success.color'))
  .property('workflow.starting.text.color', ref('danger.color'))
  .property('workflow.overdue.background.color', '#fff0f0')
  .property('workflow.overdue.text.color', offsetColor('danger.color', 10))

  /**
   * Expandable
   */
  .property('expandable.border.radius', ref('inner.radius'))
  .property('expandable.border.color', offsetColor('background.color', 10))
  .property('expandable.hover.background.color', ref('card.background.color'));
